import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addDays, eachDate, rentalDays, todayInTashkent, type DateOnly } from '@cr/shared';
import { haptic } from '@/lib/telegram';
import { currentLocale } from '@/lib/i18n';

export interface Period {
  start: DateOnly;
  end: DateOnly | null;
}

const WEEKDAYS: Record<string, string[]> = {
  ru: ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'],
  uz: ['du', 'se', 'ch', 'pa', 'ju', 'sh', 'ya'],
  en: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
};

const MONTHS: Record<string, string[]> = {
  ru: [
    'Январь',
    'Февраль',
    'Март',
    'Апрель',
    'Май',
    'Июнь',
    'Июль',
    'Август',
    'Сентябрь',
    'Октябрь',
    'Ноябрь',
    'Декабрь',
  ],
  uz: [
    'Yanvar',
    'Fevral',
    'Mart',
    'Aprel',
    'May',
    'Iyun',
    'Iyul',
    'Avgust',
    'Sentabr',
    'Oktabr',
    'Noyabr',
    'Dekabr',
  ],
  en: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
};

/**
 * Календарь выбора периода аренды.
 *
 * Занятые дни приходят с сервера: в них у вариации не осталось свободных
 * единиц. Диапазон, внутри которого есть хотя бы один занятый день, выбрать
 * нельзя — вещь всё равно не выдадут.
 */
export function RentalCalendar({
  freeByDate,
  period,
  onChange,
  minDays,
  maxDays,
  monthsToShow = 2,
}: {
  freeByDate: Map<DateOnly, number>;
  period: Period | null;
  onChange: (period: Period | null) => void;
  minDays: number;
  maxDays: number;
  monthsToShow?: number;
}) {
  const { t } = useTranslation();
  const today = todayInTashkent();
  const [offset, setOffset] = useState(0);

  const locale = currentLocale();
  const weekdays = WEEKDAYS[locale] ?? WEEKDAYS['ru']!;
  const months = MONTHS[locale] ?? MONTHS['ru']!;

  const isBusy = (date: DateOnly) => (freeByDate.get(date) ?? 0) <= 0;
  const isPast = (date: DateOnly) => date < today;

  /** Диапазон годится, только если свободен каждый день внутри него. */
  const rangeIsFree = (from: DateOnly, to: DateOnly) =>
    eachDate(from, to).every((date) => !isBusy(date));

  function handlePick(date: DateOnly) {
    if (isPast(date) || isBusy(date)) return;
    haptic.select();

    // Первый тап или перезапуск выбора
    if (!period || period.end !== null || date < period.start) {
      onChange({ start: date, end: null });
      return;
    }

    const days = rentalDays(period.start, date);

    if (days < minDays || days > maxDays || !rangeIsFree(period.start, date)) {
      onChange({ start: date, end: null });
      return;
    }

    onChange({ start: period.start, end: date });
  }

  const grids = useMemo(() => {
    const base = new Date(`${today}T00:00:00Z`);

    return Array.from({ length: monthsToShow }, (_, i) => {
      const cursor = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + offset + i, 1));
      const year = cursor.getUTCFullYear();
      const month = cursor.getUTCMonth();
      const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

      // Неделя начинается с понедельника
      const leading = (new Date(Date.UTC(year, month, 1)).getUTCDay() + 6) % 7;

      const cells: Array<DateOnly | null> = Array.from({ length: leading }, () => null);
      for (let day = 1; day <= daysInMonth; day++) {
        cells.push(
          `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` as DateOnly,
        );
      }

      return { title: `${months[month]} ${year}`, cells };
    });
  }, [offset, monthsToShow, today, months]);

  const inRange = (date: DateOnly) =>
    period?.end ? date > period.start && date < period.end : false;

  const isEdge = (date: DateOnly) => date === period?.start || date === period?.end;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <button
          onClick={() => setOffset((o) => Math.max(0, o - 1))}
          disabled={offset === 0}
          className="rounded-lg px-3 py-1 text-lg disabled:opacity-30"
          aria-label="prev"
        >
          ‹
        </button>
        <span className="text-sm text-muted">
          {period?.end
            ? t('product.days', { count: rentalDays(period.start, period.end) })
            : period
              ? t('product.selectEnd')
              : t('product.selectStart')}
        </span>
        <button
          onClick={() => setOffset((o) => o + 1)}
          className="rounded-lg px-3 py-1 text-lg"
          aria-label="next"
        >
          ›
        </button>
      </div>

      {grids.map((grid) => (
        <div key={grid.title}>
          <p className="mb-2 text-center text-sm font-medium">{grid.title}</p>

          <div className="mb-1 grid grid-cols-7 gap-1 text-center text-[11px] text-muted">
            {weekdays.map((day) => (
              <span key={day}>{day}</span>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {grid.cells.map((date, index) => {
              if (!date) return <span key={`empty-${index}`} />;

              const disabled = isPast(date) || isBusy(date);
              const edge = isEdge(date);
              const between = inRange(date);

              return (
                <button
                  key={date}
                  onClick={() => handlePick(date)}
                  disabled={disabled}
                  className={`aspect-square rounded-lg text-sm transition ${
                    edge
                      ? 'bg-bordo font-semibold text-white'
                      : between
                        ? 'bg-bordo/25'
                        : disabled
                          ? 'text-muted/40 line-through'
                          : 'bg-surface active:opacity-60'
                  }`}
                >
                  {Number(date.slice(8))}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <p className="text-xs text-muted">{t('product.buffer')}</p>

      {period && (
        <button onClick={() => onChange(null)} className="text-sm text-bordo">
          {t('catalog.reset')}
        </button>
      )}
    </div>
  );
}

/** Ближайший свободный период нужной длины — подсказка при первом открытии. */
export function suggestPeriod(
  freeByDate: Map<DateOnly, number>,
  minDays: number,
  from: DateOnly,
  horizonDays = 60,
): Period | null {
  for (let i = 0; i < horizonDays; i++) {
    const start = addDays(from, i);
    const end = addDays(start, minDays - 1);

    if (eachDate(start, end).every((date) => (freeByDate.get(date) ?? 0) > 0)) {
      return { start, end };
    }
  }

  return null;
}
